import { NetworkController } from './networkcontroller.js'
import { CacheController } from './cachecontroller.js'
import { EventName, RCEvent } from './eventmodels.js'
import { Logger } from '../logging/logger.js'

export class EventControllerClass {

    constructor() {
        // Main event queue
        this.eventQueue = [];
        // Unique identifier for next event
        this.eventIndex = 0;
        // List of subscribers to events
        this.subscribers = [];
        // Time in milliseconds post which event loop is processed
        this.eventLoopInterval = 250;

        this.startEventLoop();
    }

    /** Start main loop */
    startEventLoop() {
        // Handler to main loop
        this.eventLoop = setInterval(() => {
            this.processEvents();
        }, this.eventLoopInterval);
    }

    /** 
     * Adds event to queue.
     * @param {RCEvent} event - Instance of Event to add.
     * @returns Returns id of the event in main queue.
     */
    addEvent(event) {
        if (!(event instanceof RCEvent)) {
            throw 'Incorrect input parameters to add event';
        }

        event.id = ++this.eventIndex;
        this.eventQueue.push(event);
        if (this.eventQueue.length === 1) {
            clearInterval(this.eventLoop);
            this.startEventLoop();
        }
        return event.id;
    }

    /**
     * Removes given event from event queue.
     * @param {RCEvent} event - Instance of Event to remove.
     */
    removeEvent(event) {
        const index = this.eventQueue.indexOf(event);
        if (index > -1) {
            this.eventQueue.splice(index, 1);
            Logger.log("Removing event = ", `${event.name} ${event.data ? (event.data.path ? event.data.path : event.data.cacheKey) : null}`);
        } else {
            Logger.log("Event not found")
        }

    }

    /**
     * Runs through the event loop to process the events
     * by forwarding events to respective module.
     */
    processEvents() {
        // First clear the event loop ticking
        clearInterval(this.eventLoop)

        // Create a clone of the queue to process
        const processQueue = this.eventQueue.slice();

        processQueue.forEach(event => {
            Logger.log("Processing event = ", `${event.name} ${event.data ? (event.data.path ? event.data.path : event.data.cacheKey) : null}`);
            switch (event.name) {
                case EventName.INIT:
                    NetworkController.processEvent(event)
                    this.removeEvent(event)
                    break;

                case EventName.NETWORK:
                    /* If event is scheduled, check if the time to process it has reached.
                     * Process the event but don't remove from queue.
                     */
                    if (event.data.isScheduled) {
                        if (this.shouldProcessEvent(event)) {
                            NetworkController.processEvent(event);
                            event.data.processedTimeStamp = new Date().getTime();
                        }
                    } else {
                        /* If event is not scheduled then process it and remove from queue */
                        NetworkController.processEvent(event);
                        this.removeEvent(event);
                    }
                    break;

                case EventName.CACHE:
                    CacheController.processEvent(event);
                    this.removeEvent(event)
                    break;

                case EventName.SUBSCRIBE:
                    this.subscribers.push(event);
                    this.removeEvent(event);
                    break;

                case EventName.PUBLISH:
                    this.processPublishEvent(event);
                    this.removeEvent(event);
                    break;

                default: break;
            }
        });

        // Resume ticker if queue is not empty
        if (this.eventQueue.length > 0) {
            this.startEventLoop();
        }
    }

    processPublishEvent(publishEvent) {
        const subscribersList = this.subscribers.slice();
        subscribersList.forEach(subscribedEvent => {
            if (publishEvent.data.eventType === subscribedEvent.data.eventType) {
                subscribedEvent.callback.onSuccess(publishEvent.data.result);
                if (subscribedEvent.data.once)
                    this.removeSubscriber(subscribedEvent)
            }
        })
    }

    removeSubscriber(subscribedEvent) {
        const index = this.subscribers.indexOf(subscribedEvent);
        if (index > -1) {
            this.subscribers.splice(index, 1);
        }
    }

    /**
     * Returns true if time elapsed is equal to the frequency of the observer.
     * @param {RCEvent} event - Instance of Event under process.
     */
    shouldProcessEvent(event) {
        const previousTimeStamp = event.data.processedTimeStamp;
        return previousTimeStamp === undefined || ((new Date().getTime() - previousTimeStamp) > event.data.frequency);
    }

}
export const EventController = new EventControllerClass()